import { useState } from 'react'
import { Abrufausfall, useAbruf } from './Abruf'
import { api } from './api'
import { Dialogfenster } from './Dialogfenster'
import { meldeErfolg } from './Erfolgsstelle'
import { fachlicherSatz } from './Fehlersatz'
import { useDarf } from './Rechte'
import { periodenname, t } from './sprache'
import type { Sprachschluessel } from './sprache'
import type { Periodenschluss } from './typen'

type Handgriff = 'schliessen' | 'wiedereroeffnen' | 'zuruecknehmen'

/**
 * **Die Handgriffe am Schloss** im Konzernabschluss-Monitor (2.13 Abschnitt
 * 20.7, R1690–R1694): einen Monat schließen, wiedereröffnen, seine Freigabe
 * zurücknehmen. Freigeben steht darüber, beim Urteil.
 *
 * **Jeder Handgriff verlangt einen Satz** (2.13 Abschnitt 6): Er steht danach
 * im Nachweis neben dem Vorgang. Wer zurücknimmt, sieht vor dem Bestätigen
 * jeden Monat, der seine Freigabe verliert (R1693) — ein Klick kann viele
 * Freigaben zurücknehmen, und das soll keiner erst hinterher merken.
 */
export function Schloss({
  kreis,
  ledger,
  periode,
  freigabestatus,
  aufGeaendert,
}: {
  kreis: string
  ledger: string
  periode: number
  /** Der Zustand der Freigabe dieses Monats — `freigegeben`, `offen` … */
  freigabestatus: string | null
  /** Nach einem Handgriff: das Urteil neu holen. */
  aufGeaendert: () => void
}) {
  const darf = useDarf()
  // Der Zustand des Monats ist der Bestand dieses Bausteins — ein Hauptabruf
  // (R1211): Kam er nicht, steht das da, und kein Knopf tut so, als wäre der
  // Monat offen.
  const abruf = useAbruf<Periodenschluss[]>(() => api.periodenschluesse(kreis), [kreis])
  const schluesse = abruf.wert
  const [offen, setOffen] = useState<Handgriff | null>(null)
  const [begruendung, setBegruendung] = useState('')
  const [betroffen, setBetroffen] = useState<number[] | null>(null)
  const [meldung, setMeldung] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const geschlossen = (schluesse ?? []).some((s) => s.periode === periode && s.geschlossen)
  const monat = periodenname(periode)

  async function oeffne(handgriff: Handgriff) {
    setOffen(handgriff)
    setBegruendung('')
    setMeldung(null)
    setBetroffen(null)
    if (handgriff === 'zuruecknehmen') {
      try {
        setBetroffen(await api.freigabeZuruecknahme(kreis, ledger, periode))
      } catch (fehler) {
        setMeldung(fachlicherSatz(fehler))
      }
    }
  }

  async function bestaetige() {
    if (!offen) {
      return
    }

    setLaeuft(true)
    setMeldung(null)
    try {
      if (offen === 'schliessen') {
        abruf.setze(await api.periodeSchliessen(kreis, periode, begruendung))
        meldeErfolg(t('schloss.erfolg.geschlossen', { periode: monat }))
      } else if (offen === 'wiedereroeffnen') {
        abruf.setze(await api.periodeWiedereroeffnen(kreis, periode, begruendung))
        meldeErfolg(t('schloss.erfolg.wiedereroeffnet', { periode: monat }))
      } else {
        const monate = await api.freigabeZuruecknehmen(kreis, ledger, periode, begruendung)
        meldeErfolg(t('schloss.erfolg.zurueckgenommen', { liste: monate.map(periodenname).join(', ') }))
      }
      setOffen(null)
      aufGeaendert()
    } catch (fehler) {
      setMeldung(fachlicherSatz(fehler))
    } finally {
      setLaeuft(false)
    }
  }

  if (schluesse === null) {
    return abruf.ausgefallen ? <Abrufausfall abruf={abruf} /> : null
  }

  return (
    <div className="schloss">
      <h2>{t('schloss.titel')}</h2>
      <p>
        {t(geschlossen ? 'schloss.zustand.geschlossen' : 'schloss.zustand.offen', { periode: monat })}{' '}
        <span className="gedaempft">
          {t(geschlossen ? 'schloss.zustand.geschlossen.folge' : 'schloss.zustand.offen.folge')}
        </span>
      </p>

      <div className="aktionsreihe">
        {!geschlossen && darf('periodeSchliessen') && (
          <button type="button" onClick={() => void oeffne('schliessen')}>
            {t('schloss.schliessen')}
          </button>
        )}
        {geschlossen && darf('periodeWiedereroeffnen') && (
          <button type="button" onClick={() => void oeffne('wiedereroeffnen')}>
            {t('schloss.wiedereroeffnen')}
          </button>
        )}
        {freigabestatus === 'freigegeben' && darf('freigabeZuruecknehmen') && (
          <button type="button" className="gefahr" onClick={() => void oeffne('zuruecknehmen')}>
            {t('schloss.zuruecknehmen')}
          </button>
        )}
      </div>

      {offen && (
        <Dialogfenster
          titel={t(`schloss.${offen}` as Sprachschluessel)}
          weite="schmal"
          aufSchliessen={() => setOffen(null)}
          fuss={
            <>
              <button
                type="button"
                className={offen === 'zuruecknehmen' ? 'haupt gefahr' : 'haupt'}
                disabled={laeuft || begruendung.trim() === '' || (offen === 'zuruecknehmen' && !betroffen?.length)}
                onClick={() => void bestaetige()}
              >
                {t(`schloss.${offen}.bestaetigen` as Sprachschluessel)}
              </button>
              <button type="button" onClick={() => setOffen(null)}>
                {t('kachel.abbrechen')}
              </button>
            </>
          }
        >
          {/*
            **Im Dezember nur das Wiedereröffnen** (R1691, R120): Der Januar
            beginnt ein neues Geschäftsjahr, seine Salden tragen keine
            Korrektur des Dezember. Bis zum 2026-10-14 nannte die Frage auch
            im Dezember den Folgemonat — anders als die Ablehnung.
          */}
          <p>
            {t(
              (offen === 'schliessen' && periode % 100 === 12
                ? 'schloss.schliessen.frage.jahresende'
                : `schloss.${offen}.frage`) as Sprachschluessel,
              { periode: monat },
            )}
          </p>
          {offen === 'zuruecknehmen' && betroffen && (
            <>
              {/* Jeder Monat, der seine Freigabe verliert (R1693). */}
              <p>{t('schloss.zuruecknehmen.betroffen')}</p>
              <ul className="betroffen">
                {betroffen.map((p) => (
                  <li key={p}>{periodenname(p)}</li>
                ))}
              </ul>
            </>
          )}
          <label className="feld">
            <span>{t('schloss.begruendung')}</span>
            <textarea rows={3} value={begruendung} onChange={(e) => setBegruendung(e.target.value)} />
          </label>
          {meldung && <p className="meldung">{meldung}</p>}
        </Dialogfenster>
      )}
    </div>
  )
}
